import { motion } from "framer-motion";
import React from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
  selectAuthInitialized,
  selectAuthLoading,
  selectUser,
} from "src/store/authSlice";
import { useOperatorAuthStore } from "src/store/operatorAuthStore";
import { Constants } from "../constant";
import { useLaunchTransition } from "../hooks/useLaunchTransition";
import { useOperatorSession } from "../hooks/useOperatorSession";
import AnnouncementCenter from "./AnnouncementCenter";
import OperatorAuthGate from "./OperatorAuthGate";
import RocketLaunchLoader from "./RocketLaunchLoader";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const user = useSelector(selectUser);
  const initialized = useSelector(selectAuthInitialized);
  const loading = useSelector(selectAuthLoading);

  const token = useOperatorAuthStore((s) => s.token);
  const tokenEmail = useOperatorAuthStore((s) => s.tokenEmail);

  // The operator (iv3api) token is valid when it exists and belongs to the
  // signed-in account. Expiry is NOT part of this check — an expired token is
  // refreshed on the next 401 by the client interceptor. This keeps the gate
  // from getting stuck on tokens that carry no readable `exp`.
  const operatorValid =
    Boolean(token.trim()) &&
    tokenEmail != null &&
    user?.email != null &&
    tokenEmail === user.email.trim().toLowerCase();

  const googlePending = !initialized || loading;
  const needsOtp = !googlePending && !!user && !operatorValid;
  const canEnter = !googlePending && !!user && operatorValid;

  // The rocket launch should play only once BOTH factors are satisfied, so we
  // treat the OTP step as part of "pending".
  const { showOverlay, showContent, launchReady, onLaunchComplete } =
    useLaunchTransition(googlePending || needsOtp, canEnter);

  // Keep the token fresh in the background once fully signed in (for tokens
  // that expose an expiry; others refresh lazily via the 401 interceptor).
  useOperatorSession(canEnter);

  if (!googlePending && !user) {
    return <Navigate to={Constants.LOGIN_ROUTE} replace />;
  }

  // Google is authenticated but the operator session isn't — show the OTP
  // screen with no rocket and no app content behind it.
  if (needsOtp) {
    return <OperatorAuthGate />;
  }

  return (
    <>
      {canEnter && (
        <motion.div
          className="min-h-screen"
          initial={false}
          animate={{ opacity: showContent ? 1 : 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          aria-hidden={!showContent}
        >
          <AnnouncementCenter />
          {children}
        </motion.div>
      )}

      {showOverlay && (
        <RocketLaunchLoader
          ready={launchReady}
          onLaunchComplete={onLaunchComplete}
        />
      )}
    </>
  );
};

export default ProtectedRoute;
